export { getCockpitMetrics } from './getCockpitMetrics'
export { getLaborMetrics } from './getLaborMetrics'
export { getLaborTableMetrics } from './getLaborTableMetrics'
export { getLoyaltyMetrics } from './getLoyaltyMetrics'
export { getPnLMetrics } from './getPnLMetrics'
export { getSalesMetrics } from './getSalesMetrics'
export { getSpeedOfServiceMetrics } from './getSpeedOfServiceMetrics'
export { getSpendMetrics } from './getSpendMetrics'
export { getStoreHealthCheck } from './getStoreHealthCheck'

import axios from 'axios'

import { BASE_URL } from '@/api/api.constants'

type GetSalesBreakdownParams = {
  branchName: string
  startDate: string
  endDate: string
  limit?: number
  offset?: number
}

type SalesBreakDown = {
  id: number
  name: string
  sale_count: number
  sale_amount: number
}

type SubSubCategory = {
  id: number
  sub_category_id: number
  name: string
  children: SalesBreakDown[]
}

type SubCategory = {
  id: number
  category_id: number
  name: string
  children: SubSubCategory[]
}

export type Category = {
  id: number
  name: string
  children: SubCategory[]
}

type GetSalesBreakdown = (
  params: GetSalesBreakdownParams
) => Promise<Category[]>

// adds count and percentage to each item -> an item's count is the sum of all leaf nodes' sale_count
const convertToTree = (data: Category[]) => {
  const calculateMetrics = (node: any) => {
    if (!node.children || node.children.length === 0) {
      return node.sale_count || 0
    }

    let totalCount = 0
    node.children.forEach((child: any) => {
      totalCount += calculateMetrics(child)
    })

    node.sale_count = totalCount
    return totalCount
  }

  const addPercentage = (node: any, total: number) => {
    if (total > 0) {
      node.percentage = ((node.sale_count || 0) / total) * 100
    } else {
      node.percentage = 0
    }

    if (node.children && node.children.length > 0) {
      node.children.forEach((child: any) => addPercentage(child, total))
    }
  }

  const totalSoldItems = data.reduce(
    (sum, category) => sum + calculateMetrics(category),
    0
  )

  data.forEach((category) => addPercentage(category, totalSoldItems))

  return data
}

export const getSalesBreakdown: GetSalesBreakdown = async ({
  branchName,
  startDate,
  endDate,
  limit = 1000,
  offset = 0,
}) => {
  try {
    const response = await axios.get(`${BASE_URL}/sales-breakdowns/`, {
      params: {
        start_date: startDate,
        end_date: endDate,
        branch_names: branchName,
        limit,
        offset,
      },
    })
    const rawData = response.data
    return convertToTree(rawData)
  } catch (error) {
    throw error
  }
}

type GetRegionBoxMetricDataParams = {
  branches: string
  startDate: string
  endDate: string
}

type RegionBoxMetricDataReturn = {
  [key: string]: {
    title: string
    type: 'number' | 'percentage' | 'money'
    leftLabel: string
    rightLabel: string
    leftValue: number
    rightValue: number
  }[]
}

type GetRegionBoxMetricData = (
  params: GetRegionBoxMetricDataParams
) => Promise<RegionBoxMetricDataReturn>
export const getRegionBoxMetricData: GetRegionBoxMetricData = async ({
  branches,
  startDate,
  endDate,
}) => {
  try {
    const response = await axios.get(`${BASE_URL}/region-box-metrics/`, {
      params: {
        branch_names: branches,
        start_date: startDate,
        end_date: endDate,
      },
    })
    const rawData = response.data
    if (rawData.totalDiscountsRatio) {
      rawData.totalDiscountsRatio = rawData.totalDiscountsRatio.filter(
        (item: any) => item.title !== 'Discount Rate'
      )
    }
    return rawData
  } catch (error) {
    throw error
  }
}
